import type { CancellationPolicyWindowUnit } from '@shared/core/cancellation-policy'
import { Dispatch, SetStateAction, useCallback } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type {
  CancellationPolicyFormErrors,
  CancellationPolicyFormValues,
} from '../types/cancellation-policy-form.types'
import { buildDefaultPolicyText, parsePolicyWindowValue } from '../utils/cancellation-policy-form.util'

const DIRTY = { shouldDirty: true }

/** The text follows the window until the owner edits it; after that it is never changed for them. */
export function usePolicyFieldHandlers(
  form: UseFormReturn<CancellationPolicyFormValues>,
  setErrors: Dispatch<SetStateAction<CancellationPolicyFormErrors>>,
) {
  const { getValues, setValue } = form

  const onNoticeChange = useCallback(
    (value: string, unit: CancellationPolicyWindowUnit) => {
      setErrors((current) => ({ ...current, notice_value: undefined }))
      const parsed = parsePolicyWindowValue(value, unit)
      if (getValues('policy_text_edited') || parsed === null) return
      setValue('policy_text', buildDefaultPolicyText(parsed, unit), DIRTY)
    },
    [getValues, setErrors, setValue],
  )

  const onTextChange = useCallback(
    (text: string) => {
      setErrors((current) => ({ ...current, policy_text: undefined }))
      setValue('policy_text', text, DIRTY)
      setValue('policy_text_edited', true, DIRTY)
    },
    [setErrors, setValue],
  )

  return { onNoticeChange, onTextChange }
}
